import { defineStore } from "pinia";
import { ref } from 'vue'

import { useUserStore } from "./user";
import { useNotificationStore } from './notification';

import { fetchFromBackend } from "./helpers/backend.mjs";

export const useBusinessPartnersCountStore = defineStore("businessPartnersCount", {
  state: () => {
    return {
      businessPartnerLists: [], // a count for each list of business partners
      isLoadingCount: {}, // Map: signature -> boolean
    }
  },
  getters: {
    isLoadingBySignature: (state) => (signature) => {
      return !!state.isLoadingCount[signature];
    },
    businessPartnersListsCountBySignature: (state) => (signature) => {
      const businessPartnerCountExists = state.businessPartnerLists.find((list) => list.signature === signature);
      // console.log('businessPartnersListsCountBySignature', signature, businessPartnerCountExists)
      return (businessPartnerCountExists) ? businessPartnerCountExists.count : 0;
    },
    namedFilter: (state) => (name) => {
      const userStore = useUserStore();
      // console.log('requesting namedFilter', name)
      let filter = {
        idCompany: userStore.currentSelection?.campaign?.company?.id,
        idCampaign: userStore.currentSelection?.campaign?.id,
        idSalesAgent: (userStore.currentSelection?.scope === 'salesAgent') ? userStore.currentSelection?.salesAgent?.id : -1,
        idSalesTeam: (userStore.currentSelection?.scope === 'salesTeam') ? userStore.currentSelection?.salesTeam?.id : -1,
      };
      switch (name) {
        case 'businessPartnerListCount': {
          // searchCriteria hardcoded wie in der Listen-View
          const searchCriteria = [
            { field: 'idBusinessPartnerAboStatus', value: -1 },
            { field: 'idBusinessPartnerCityZip', value: -1 },
            { field: 'nameBusinessPartnerSector', value: '' }
          ];
          filter = Object.assign({}, filter, {
            page: 1,
            pages: 1,
            pageSize: 24,
            // id: -1,
            idBusinessPartnerList: -1,
            idBusinessPartnerStatus: null,
            businessPartnerSalesPitchStatus: { id: -1, dateStart: null, dateEnd: null },
            // idBusinessPartnerProduct: -1,
            idBusinessPartnerProduct: [], // Array of {id, include}
            idBusinessPartnerProductSegment: [],
            idBusinessPartnerPotential: -1,
            search: { mode: 'name', string: '', wholeWord: 0 },
            productGroupStates: "GS:2,TG:2,TB:2,OMP:2,SUM:2",
            invoice: { dateEnd: null, dateStart: null },
            salesValues: { gs: null, oe: null, omp: null, tb: null, total: null },
            revenueMax: null,
            revenueMin: null,
            idBusinessPartnerOrder: -1,
            idSalesTeam: userStore.currentSelection?.salesTeam?.id || -1,
            signature: `${userStore.currentSelection?.salesAgent?.id}:${userStore.currentSelection.campaign.id}#businessPartnerListCount`,
            summary: 'Alle BusinessPartner-Listen'
          });
          // searchCriteria NICHT im Filter, sondern beim getByFilter-Aufruf als 2. Parameter übergeben
          filter._searchCriteria = searchCriteria; // temporär merken
          break;
        }
        case 'eigenrechercheUnbeendetCount':
          filter = Object.assign({}, filter, {
            page: 1,
            pages: 1,
            pageSize: 101,
            id: -1,
            idBusinessPartnerList: "seg:7", // Eigenrecherche
            idBusinessPartnerStatus: null,
            businessPartnerSalesPitchStatus: { id: 10, dateStart: null, dateEnd: null },
            idBusinessPartnerOrder: 3,
            // dateStart: new Date().toISOString().split('T')[0],
            // dateEnd: new Date().toISOString().split('T')[0],
            order: 'asc',
            idSalesTeam: userStore.currentSelection?.salesTeam?.id || -1,
            idSalesAgent: userStore.currentSelection?.salesAgent?.id || -1,
            signature: `${userStore.currentSelection?.salesAgent?.id}:${userStore.currentSelection.campaign.id}#eigenrechercheUnbeendetCount`,
            summary: 'Unbeendet: Eigenrecherche'
          });
          break;
        case 'Today:WiedervorlagenCount':
          filter = Object.assign({}, filter, {
            page: 1,
            pages: 1,
            pageSize: 2500,
            // id: -1,
            idBusinessPartnerStatus: null,
            businessPartnerSalesPitchStatus: { id: 2, dateStart: new Date().toISOString().split('T')[0], dateEnd: new Date().toISOString().split('T')[0] },
            idBusinessPartnerOrder: 3,
            order: 'asc',
            signature: `${userStore.currentSelection?.salesAgent?.id}:${userStore.currentSelection.campaign.id}#Today:WiedervorlagenCount`,
            summary: 'Today:Wiedervorlagen'
          });
          break;
        case 'Today:VorOrtTermineCount':
          filter = Object.assign({}, filter, {
            page: 1,
            pages: 1,
            pageSize: 2500,
            // id: -1,
            idBusinessPartnerStatus: null,
            businessPartnerSalesPitchStatus: { id: 3, dateStart: new Date().toISOString().split('T')[0], dateEnd: new Date().toISOString().split('T')[0] },
            idBusinessPartnerOrder: 3,
            order: 'asc',
            signature: `${userStore.currentSelection?.salesAgent?.id}:${userStore.currentSelection.campaign.id}#Today:VorOrtTermineCount`,
            summary: 'Today:VorOrtTermine'
          });
          break;
        case 'ToDo:WiedervorlagenCount':
          filter = Object.assign({}, filter, {
            page: 1,
            pages: 1,
            pageSize: 250,
            id: -2,
            idBusinessPartnerStatus: null,
            businessPartnerSalesPitchStatus: { id: 2, dateStart: null, dateEnd: null },
            idBusinessPartnerOrder: -1,
            order: 'asc',
            signature: `${userStore.currentSelection?.salesAgent?.id}:${userStore.currentSelection.campaign.id}#ToDo:WiedervorlagenCount`,
            summary: 'Wiedervorlagen'
          });
          break;
        case 'ToDo:VorOrtTermineCount':
          filter = Object.assign({}, filter, {
            page: 1,
            pages: 1,
            pageSize: 250,
            id: -2,
            idBusinessPartnerStatus: null,
            businessPartnerSalesPitchStatus: { id: 3, dateStart: null, dateEnd: null },
            idBusinessPartnerOrder: -1,
            order: 'asc',
            signature: `${userStore.currentSelection?.salesAgent?.id}:${userStore.currentSelection.campaign.id}#ToDo:VorOrtTermineCount`,
            summary: 'Termin Feedback'
          });
          break;
        default:
          break;
      }
      // console.log('namedFilter', name, '=>', filter)
      return filter;
    }
  },
  actions: {
    /** start a search with a filter, i.e. { idBusinessPartnerList: "list:12" } for businessPartners either from Dashboard or from businessPartnerList
     * @async
     * @param {Object} filter - The filter object to search for businessPartners
     * @param {Array} searchCriteria - Additional search criteria to filter the businessPartners
    */
    async getByFilter(filter, searchCriteria = []) {
      const endpoint = "businessPartners/v1/getListByFilter";
      // Setze Loading-Flag für diese Signatur
      if (filter && filter.signature) this.isLoadingCount[filter.signature] = true;
      const userStore = useUserStore();
      const notificationStore = useNotificationStore();

      // see if there is existing data if it exists and its update property is less then 6 hours old and it matches the desired page
      const storedBusinessPartnerList = this.businessPartnerLists.find((businessPartnerList) => businessPartnerList.signature === filter.signature);

      // searchCriteria ggf. aus Filter holen (nur für businessPartnerListCount)
      const f = { ...filter };
      let effectiveSearchCriteria = searchCriteria;
      if (Array.isArray(f._searchCriteria)) {
        effectiveSearchCriteria = f._searchCriteria;
        delete f._searchCriteria;
      }

      // Neue Zähl-Logik: immer nur Gesamt-Trefferanzahl ermitteln
      // (z.B. Potential & BK, WVL/Termine Today/ToDo, Eigenrecherche-Counter)
      if (!f.mode) {
        f.mode = "countBusinessPartner";
      }

      const payload = {
        id: userStore.id,
        filter: f,
        view: {
          page: 1,
          // Für die reine Zählung ist page/pageSize nachrangig,
          // wird aber vom Backend erwartet – wir verwenden hier die gleiche Logik wie zuvor.
          pageSize: f.pageSize * f.pages,
          searchCriteria: effectiveSearchCriteria,
          sort: {
            field: f.idBusinessPartnerOrder,
          }
        },
      };

      let response = null;
      try {
        response = await fetchFromBackend(endpoint, payload);
        // notificationStore.addNotification(`BusinessPartner-Liste "${filter.summary}" erfolgreich geladen!`, 'success');
      } catch (error) {
        notificationStore.addNotification(`BusinessPartner-Count-Liste konnte nicht geladen werden! (${error?.message || error})`, 'error', 12);
        if (filter && filter.signature) this.isLoadingCount[filter.signature] = false;
        return false;
      }
      // Treffer-Anzahl direkt und ausschließlich aus view.countBusinessPartnerFiltered lesen
      const apiCount = (response && response.view && typeof response.view.countBusinessPartnerFiltered === 'number')
        ? response.view.countBusinessPartnerFiltered
        : null;

      if (typeof apiCount !== 'number' || apiCount < 0) {
        notificationStore.addNotification('Keine BusinessPartner-Count-Liste gefunden!', 'error', 13);
        if (filter && filter.signature) this.isLoadingCount[filter.signature] = false;
        return false;
      }

      const finalCount = apiCount;

      // if the data does not exists create it, if not update or expand it
      if (!storedBusinessPartnerList) {
        this.businessPartnerLists.push({
          signature: filter.signature,
          filter: filter,
          searchCriteria: searchCriteria,
          summary: response.filter.summary,
          count: finalCount,
          countDistinct: finalCount,
          update: Date.now()
        });
      } else {
        storedBusinessPartnerList.update = Date.now();
        storedBusinessPartnerList.summary = response.filter.summary;
        storedBusinessPartnerList.count = finalCount;
        storedBusinessPartnerList.countDistinct = finalCount;
      }
      // Lade-Flag zurücksetzen
      if (filter && filter.signature) this.isLoadingCount[filter.signature] = false;
    },
    /** at campaign change or in certain intervals, the businessPartnerList data needs to be refreshed to reflect up to date 'Wiedervorlagen', 'vor Ort Termine' and 'Sugar Aktionen' 
     * @async
    */
    async refresh() {
      const userStore = useUserStore();
      const notificationStore = useNotificationStore();
      // if (userStore.currentSelection.scope !== 'salesAgent') return false;
      const taskList = [
        this.getByFilter(this.namedFilter('ToDo:WiedervorlagenCount')),
        this.getByFilter(this.namedFilter('ToDo:VorOrtTermineCount')),
        this.getByFilter(this.namedFilter('Today:WiedervorlagenCount')),
        this.getByFilter(this.namedFilter('Today:VorOrtTermineCount')),
      ];
      console.log('REFRESH: CountStore')
      userStore.currentSelection.businessPartnerCount.isLoading = true;
      try {
        await Promise.all(taskList);
        // notificationStore.addNotification('BusinessPartner-Count-Listen erfolgreich aktualisiert!', 'success');
      } catch (error) {
        notificationStore.addNotification(`BusinessPartner-Count-Listen-Refresh fehlgeschlagen! (${error?.message || error})`, 'error', 14);
      }
      userStore.currentSelection.businessPartnerCount.isLoading = false;
    },
  },
});